import { Fragment, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Link, useNavigate } from '@tanstack/react-router';
import { AppLayout } from './AppLayout';
import { TopBarBackButton } from './topbar/TopBarBackButton';
import { DesktopPage } from '@/components/desktop/DesktopPage';
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader';
import { useIsDesktop } from '@/components/ui/use-mobile';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SwipeableRow } from '@/components/ui/swipeable-row';
import { parseNotificationLink } from '@/lib/notificationLink';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Archive,
  Bell,
  CalendarDays,
  Inbox as InboxIcon,
  Info,
  Mail,
  MailOpen,
  Megaphone,
  MessageSquare,
  MoreVertical,
  Plane,
  Wrench,
  type IconComponent,
} from '@/components/icons';
import { EmptyState } from '@/components/EmptyState';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkNotificationAsUnread,
  useMarkAllNotificationsAsRead,
  useSetNotificationArchived,
} from '@/lib/convex-hooks';
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember';

dayjs.extend(relativeTime);

type InboxTab = 'notices' | 'activity';

interface InboxListProps {
  tab?: InboxTab;
  archived?: boolean;
}

// Categories that belong in the Notices tab (operational/broadcast).
// Anything not in this set — and any row without a `category` whose `type` isn't in NOTICE_TYPES — falls into Activity.
const NOTICE_CATEGORIES = new Set(['document', 'flight', 'maintenance', 'announcement', 'task', 'roster', 'trip', 'pax']);

// Legacy fallback for rows that predate the `category` field.
const NOTICE_TYPES = new Set(['announcement', 'flight', 'roster']);

const isNotice = (item: { category?: string; type: string }) =>
  item.category ? NOTICE_CATEGORIES.has(item.category) : NOTICE_TYPES.has(item.type);

// Chat lives in Conversations with its own unread state — chat rows are no
// longer created, and legacy `message` rows are excluded here (and server-side
// in convex notifications.list) so they never reach the inbox.
const isChatItem = (item: { category?: string; type: string }) =>
  item.category ? item.category === 'message' : item.type === 'message';

const TYPE_ICON: Record<string, IconComponent> = {
  announcement: Megaphone,
  flight: Plane,
  roster: CalendarDays,
  message: MessageSquare,
  mention: MessageSquare,
  system: Info,
};

// Category-specific overrides for rows whose coarse `type` doesn't pick the
// right icon (e.g. maintenance rows carry type "flight").
const CATEGORY_ICON: Record<string, IconComponent> = {
  maintenance: Wrench,
};

function formatTimestamp(createdAt: number): string {
  const date = dayjs(createdAt);
  const now = dayjs();
  const diffMinutes = now.diff(date, 'minute');
  const diffHours = now.diff(date, 'hour');
  const diffDays = now.diff(date, 'day');

  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return date.format('dddd');
  return date.format('DD/MM/YYYY');
}

interface InboxItem {
  _id: string;
  type: string;
  category?: string;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdAt: number;
}

interface InboxRowProps {
  notification: InboxItem;
  isSwipeOpen: boolean;
  onSwipeOpenChange: (open: boolean) => void;
  onToggleRead: () => void;
  onArchive: () => void;
  onClick: () => void;
  archived?: boolean;
}

function InboxRow({
  notification,
  isSwipeOpen,
  onSwipeOpenChange,
  onToggleRead,
  onArchive,
  onClick,
  archived = false,
}: InboxRowProps) {
  const Icon =
    (notification.category ? CATEGORY_ICON[notification.category] : undefined) ??
    TYPE_ICON[notification.type] ??
    Bell;

  // Archive view: a single "Unarchive" action. Inbox: read/unread toggle + archive.
  const swipeProps = archived
    ? {
        onDelete: onArchive,
        actionLabel: 'Unarchive',
        actionIcon: <Archive />,
        actionClassName: 'bg-content-primary-subtle',
      }
    : {
        onEdit: onToggleRead,
        editLabel: notification.isRead ? 'Unread' : 'Read',
        editIcon: notification.isRead ? <Mail /> : <MailOpen />,
        editClassName: 'bg-content-info-bold',
        onDelete: onArchive,
        actionLabel: 'Archive',
        actionIcon: <Archive />,
        actionClassName: 'bg-content-primary-subtle',
      };

  return (
    <SwipeableRow
      variant="icon"
      isOpen={isSwipeOpen}
      onOpenChange={onSwipeOpenChange}
      {...swipeProps}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex w-full gap-4 items-center text-left bg-surface-L0 min-h-9 pb-1 active:opacity-60 transition-opacity"
      >
        <Icon className="size-6 shrink-0 text-content-primary-bold" />
        <div className="flex flex-col gap-1 flex-1 min-w-0 items-start">
          <div className="flex items-center gap-[7px]">
            <span className="text-sm font-semibold text-content-primary-bold leading-5">
              {notification.title}
            </span>
            {!notification.isRead && (
              <span className="shrink-0 size-[9px] rounded-full bg-content-info-bold" />
            )}
          </div>
          <p className="text-sm text-content-primary-subtle leading-5 line-clamp-2">
            {notification.message}
          </p>
          <p className="text-xs font-semibold text-content-primary-subtle leading-4">
            {formatTimestamp(notification.createdAt)}
          </p>
        </div>
      </button>
    </SwipeableRow>
  );
}

export function InboxList({ tab = 'notices', archived = false }: InboxListProps) {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { data: currentUser } = useCurrentCrewMember();
  const notifications = useNotifications(currentUser?.id, undefined, archived);
  const markAsRead = useMarkNotificationAsRead();
  const markAsUnread = useMarkNotificationAsUnread();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const setArchived = useSetNotificationArchived();

  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const items = ((notifications ?? []) as InboxItem[]).filter(
    (n) => !isChatItem(n)
  );
  const notices = items.filter(isNotice);
  const activity = items.filter((n) => !isNotice(n));
  const hasUnread = items.some((n) => !n.isRead);

  const handleClick = (notification: InboxItem) => {
    if (currentUser && !notification.isRead) {
      markAsRead({ notificationId: notification._id as never, crewId: currentUser.id });
    }
    if (notification.link) {
      const { to, search } = parseNotificationLink(notification.link);
      navigate({ to: to as any, search: search as any });
      return;
    }
    navigate({
      to: '/inbox/$notificationId',
      params: { notificationId: notification._id },
    });
  };

  const handleToggleRead = (notification: InboxItem) => {
    if (!currentUser) return;
    if (notification.isRead) {
      markAsUnread({ notificationId: notification._id as never, crewId: currentUser.id });
    } else {
      markAsRead({ notificationId: notification._id as never, crewId: currentUser.id });
    }
    setOpenRowId(null);
  };

  const handleArchive = (notification: InboxItem) => {
    if (!currentUser) return;
    setArchived({
      notificationId: notification._id as never,
      crewId: currentUser.id,
      isArchived: !archived,
    });
    setOpenRowId(null);
  };

  const handleTabChange = (value: string) => {
    navigate({
      to: '/inbox',
      search: { tab: value as InboxTab },
      replace: true,
    });
    setOpenRowId(null);
  };

  const topBarRightIcon = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" roundness="round" className="size-8 p-0">
          <MoreVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={!hasUnread || !currentUser}
          onSelect={() => {
            if (currentUser) markAllAsRead({ crewId: currentUser.id });
          }}
        >
          Mark All as Read
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate({ to: '/inbox/archive' })}>
          Archived
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderList = (list: InboxItem[], emptyTitle: string, emptyDescription: string) => {
    if (list.length === 0) {
      return (
        <EmptyState
          icon={InboxIcon}
          title={emptyTitle}
          description={emptyDescription}
        />
      );
    }
    return (
      <Card className="flex flex-col gap-2 overflow-hidden">
        {list.map((notification, index) => {
          const rowId = `inbox-${notification._id}`;
          return (
            <Fragment key={notification._id}>
              {index > 0 && <div className="h-px w-full bg-border-L1" />}
              <InboxRow
                notification={notification}
                isSwipeOpen={openRowId === rowId}
                onSwipeOpenChange={(o) => setOpenRowId(o ? rowId : null)}
                onToggleRead={() => handleToggleRead(notification)}
                onArchive={() => handleArchive(notification)}
                onClick={() => handleClick(notification)}
                archived={archived}
              />
            </Fragment>
          );
        })}
      </Card>
    );
  };

  if (archived) {
    const archivedList = renderList(
      items,
      'No archived notifications',
      'Notifications you archive will show up here.'
    );

    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader
            title="Archived"
            breadcrumb={
              <span>
                <Link to="/inbox" className="hover:underline">
                  Inbox
                </Link>
                <span className="text-content-primary-subtle"> / Archived</span>
              </span>
            }
          />
          {archivedList}
        </DesktopPage>
      );
    }

    return (
      <AppLayout
        topBar={<TopBarBackButton title="Archived" />}
        wrapperClassName="bg-surface-brand-L0"
        className="pt-4"
      >
        {archivedList}
      </AppLayout>
    );
  }

  const tabsContent = (
    <Tabs value={tab} onValueChange={handleTabChange} className="gap-0">
      {/* Mobile: sticky tab header — opaque, full-width band so the list scrolls
          beneath it. The screen's top spacing lives INSIDE this band (not as
          padding on the scroll container) — sticky elements anchor at the
          scroll port's padding edge, so container padding would leave an
          unclipped strip above the band with rows showing through.
          Desktop: DesktopPage's own padding handles spacing, so no sticky band. */}
      <div
        className={cn(
          'flex items-center gap-2 pb-2',
          !isDesktop && 'sticky top-0 z-10 bg-surface-brand-L0 pt-4'
        )}
      >
        <TabsList className="w-full flex-1">
          <TabsTrigger value="notices">Notices</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>
        <Button
          variant="secondary"
          size="sm"
          roundness="round"
          aria-pressed={showUnreadOnly}
          title={showUnreadOnly ? 'Show all' : 'Show unread only'}
          onClick={() => setShowUnreadOnly((v) => !v)}
          className={cn(
            'shrink-0',
            showUnreadOnly && 'bg-background-brand-subtle text-content-brand-bold'
          )}
        >
          Unread
        </Button>
      </div>
      <TabsContent value="notices">
        {renderList(
          showUnreadOnly ? notices.filter((n) => !n.isRead) : notices,
          showUnreadOnly ? 'No unread notices' : 'No notices',
          "You're all caught up on operational updates."
        )}
      </TabsContent>
      <TabsContent value="activity">
        {renderList(
          showUnreadOnly ? activity.filter((n) => !n.isRead) : activity,
          showUnreadOnly ? 'No unread activity' : 'No activity',
          'Birthdays, mentions, and other updates will show up here.'
        )}
      </TabsContent>
    </Tabs>
  );

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title="Inbox" action={topBarRightIcon} />
        {tabsContent}
      </DesktopPage>
    );
  }

  return (
    <AppLayout
      topBar={<TopBarBackButton title="Inbox" rightElement={topBarRightIcon} />}
      wrapperClassName="bg-surface-brand-L0"
      className="pb-4"
    >
      {tabsContent}
    </AppLayout>
  );
}
